import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';

import { safeLocalStorageGetItem, safeLocalStorageSetItem } from './platformStorage';

const FIRST_TOUCH_STORAGE_KEY = 'primum_first_touch';

const UTM_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export type FirstTouchAttribution = Record<string, string>;

/**
 * Captures first-touch marketing attribution as write-once user-profile traits —
 * the per-user replacement for the legacy account-linking step that caused the
 * shared-device profile merge. Records the first attributed touch (a UTM tag or
 * external referrer) once and never overwrites it, and only when there's a real
 * signal — so an organic visit doesn't lock in an empty touch and mask a later
 * campaign. Web-only — native has no UTM tags in the URL.
 *
 * Trait keys use Mixpanel's standard first-touch property prefix while the code
 * keeps the standard concept name. They must be set explicitly: Mixpanel only
 * captures first-touch properties automatically through its client SDK, which
 * this app does not use — analytics flow server-side through Segment.
 */
export function captureFirstTouchAttribution(): void {
  if (!isWeb) {
    return;
  }

  // Write-once: never overwrite an existing first touch.
  if (safeLocalStorageGetItem(FIRST_TOUCH_STORAGE_KEY)) {
    return;
  }

  const params = new URLSearchParams(window.location.search);

  const attribution: FirstTouchAttribution = {};

  for (const key of UTM_PARAM_KEYS) {
    const value = params.get(key);

    if (value) {
      attribution[`initial_${key}`] = value;
    }
  }

  const referrer = typeof document !== 'undefined' ? document.referrer : '';

  const isExternalReferrer = Boolean(referrer) && !referrer.startsWith(window.location.origin);

  if (Object.keys(attribution).length === 0 && !isExternalReferrer) {
    return;
  }

  if (isExternalReferrer) {
    attribution.initial_referrer = referrer;
  }

  attribution.initial_landing_page = window.location.pathname;

  attribution.initial_touch_at = new Date().toISOString();

  safeLocalStorageSetItem(FIRST_TOUCH_STORAGE_KEY, JSON.stringify(attribution));
}

/**
 * Returns the stored first-touch traits, or null if none was captured.
 */
export function getFirstTouchAttribution(): FirstTouchAttribution | null {
  const raw = safeLocalStorageGetItem(FIRST_TOUCH_STORAGE_KEY);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);

    return parsed && typeof parsed === 'object' ? (parsed as FirstTouchAttribution) : null;
  } catch {
    return null;
  }
}
